import { useRef, useLayoutEffect, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';

interface SegmentedOption<V extends string = string> {
  label: string;
  value: V;
  icon?: ReactNode;
}

export type SegmentedSize = 'sm' | 'md' | 'lg';

interface SegmentedProps<V extends string = string> {
  options: (V | SegmentedOption<V>)[];
  value?: V;
  onChange?: (next: V) => void;
  style?: CSSProperties;
  className?: string;
  stretch?: boolean;
  size?: SegmentedSize;
  disabled?: boolean;
  iconOnly?: boolean;
}

// Segmented reads the shared control-size regime (tokens.css §10.1): the track
// fills the tier height and the segments stretch to it, so it lines up in a row
// with Buttons/Selects at the same `size`. Track padding is a constant 3px
// inset (not tier-driven) — segments stretch to fill the remaining height.
const OUTER_PADDING = 3;
const SIZES: Record<SegmentedSize, { height: string; gap: number; px: string; fontSize: string; itemGap: number }> = {
  sm: { height: 'var(--ds-control-h-sm)', gap: 2, px: 'var(--ds-control-px-sm)', fontSize: 'var(--ds-control-font-sm)', itemGap: 4 },
  md: { height: 'var(--ds-control-h-md)', gap: 3, px: 'var(--ds-control-px-md)', fontSize: 'var(--ds-control-font-md)', itemGap: 5 },
  lg: { height: 'var(--ds-control-h-lg)', gap: 4, px: 'var(--ds-control-px-lg)', fontSize: 'var(--ds-control-font-lg)', itemGap: 6 },
};

export function Segmented<V extends string = string>({ options, value, onChange, style, className, stretch, size = 'md', disabled = false, iconOnly = false }: SegmentedProps<V>) {
  const sz = SIZES[size];
  const opts: SegmentedOption<V>[] = options.map(o => typeof o === 'string' ? { label: o, value: o } : o);
  const containerRef = useRef<HTMLSpanElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  // Tracked in state (not mutated imperatively on the DOM) so a value switch driven by a
  // click — not a mouse event on the item itself — can't leave a stale hover background
  // behind: every render derives each item's background fresh from this plus `active`.
  const [hoveredValue, setHoveredValue] = useState<V | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current || value == null) { setPill(null); return; }
    const item = containerRef.current.querySelector<HTMLElement>(`[data-value="${CSS.escape(value)}"]`);
    if (!item) { setPill(null); return; }
    setPill({ left: item.offsetLeft, width: item.offsetWidth });
  }, [value, opts.map(o => o.value).join(',')]);

  const handleKeyDown = (e: KeyboardEvent, idx: number) => {
    if (disabled) return;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % opts.length;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + opts.length) % opts.length;
    if (next === -1) return;
    e.preventDefault();
    onChange?.(opts[next].value);
    containerRef.current?.querySelector<HTMLElement>(`[data-value="${CSS.escape(opts[next].value)}"]`)?.focus();
  };

  return (
    <span
      ref={containerRef}
      role="radiogroup"
      style={{
        display: stretch ? 'flex' : 'inline-flex', alignItems: 'stretch', gap: sz.gap,
        height: sz.height, padding: OUTER_PADDING, boxSizing: 'border-box',
        borderRadius: 'var(--ds-radius-sm)',
        background: 'hsl(var(--ds-neutral-bg))',
        border: '1px solid var(--ds-control-border)',
        position: 'relative',
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
      className={className}
    >
      {/* Sliding indicator — renders behind the text items. Hover-brightens when
          the pointer is over the active segment, since that segment's own span
          is transparent (text-only) — the fill a hover needs to affect lives here. */}
      {pill && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: OUTER_PADDING, height: `calc(100% - ${OUTER_PADDING * 2}px)`,
            borderRadius: 'var(--ds-radius-xs)',
            background: 'hsl(var(--ds-brand-purple))',
            boxShadow: '0 4px 12px -6px hsl(285 75% 50% / 0.7)',
            filter: hoveredValue === value ? 'brightness(var(--ds-hover-brightness))' : undefined,
            transition: `left var(--ds-dur-fast) var(--ds-ease-knob), width var(--ds-dur-fast) var(--ds-ease-knob), filter var(--ds-dur-fast) var(--ds-ease-soft)`,
            left: pill.left,
            width: pill.width,
            pointerEvents: 'none',
          }}
        />
      )}

      {opts.map((o, idx) => {
        const active = o.value === value;
        const hovered = hoveredValue === o.value;
        return (
          <span
            key={o.value}
            data-value={o.value}
            className="ds-focusable"
            role="radio"
            aria-checked={active}
            aria-disabled={disabled || undefined}
            aria-label={iconOnly ? o.label : undefined}
            title={iconOnly ? o.label : undefined}
            tabIndex={disabled ? -1 : active ? 0 : -1}
            onClick={() => { if (!disabled) onChange?.(o.value); }}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            style={{
              position: 'relative', zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sz.itemGap,
              padding: '0 ' + sz.px, borderRadius: 'var(--ds-radius-xs)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--ds-font-body)', fontWeight: 600, fontSize: sz.fontSize,
              whiteSpace: 'nowrap',
              color: active ? '#fff' : 'hsl(var(--ds-text-meta))',
              // Active segment's fill is the sliding pill behind it (brightened via `filter`
              // above on hover instead) — its own background always stays transparent so it
              // never layers a second tint on top of the pill. Inactive segments tint directly.
              background: !active && hovered ? 'var(--ds-ghost-hover-bg)' : 'transparent',
              transition: `color var(--ds-dur-fast) ease, background var(--ds-dur-fast) var(--ds-ease-soft)`,
              outline: 'none',
              ...(stretch ? { flex: 1 } : {}),
            }}
            onMouseEnter={() => { if (!disabled) setHoveredValue(o.value); }}
            onMouseLeave={() => setHoveredValue((v) => (v === o.value ? null : v))}
          >
            {o.icon}
            {!iconOnly && o.label}
          </span>
        );
      })}
    </span>
  );
}
